import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { connectedUserAtom } from "../../../middlewares/AuthMiddleware";
import flotteService from "../../../services/flotteService";
import { ROLES } from "../../../config/accessEnum";

const formatDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary ml-2"
      onClick={handleCopy}
      title={label}
      style={{ fontSize: "0.8em", padding: "2px 8px" }}
    >
      <i className={`fas ${copied ? "fa-check" : "fa-copy"} mr-1`}></i>
      {copied ? "Copié" : "Copier"}
    </button>
  );
};

const Import = () => {
  const [connectedUser] = useAtom(connectedUserAtom);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const fileInputRef = useRef(null);

  // LS-727 — l'import est ouvert à tous les admins, pas seulement aux super admins
  const peutImporter = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(
    connectedUser?.role,
  );

  const fetchHistory = async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await flotteService.listImportsFactures();
      setHistory(Array.isArray(data) ? data : data?.data || []);
    } catch (err) {
      setHistoryError(
        err.response?.data?.message ||
          err.message ||
          "Impossible de charger l'historique des imports.",
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (peutImporter) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peutImporter]);

  // Vérification du rôle (SUPER_ADMIN ou ADMIN)
  if (!peutImporter) {
    return (
      <div className="container-fluid mt-4">
        <div className="alert alert-danger">
          <i className="fas fa-lock mr-2"></i>
          Accès refusé. Cette page est réservée aux administrateurs.
        </div>
      </div>
    );
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile || null);
    setResult(null);
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setResult(null);
      setError(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSubmit = async () => {
    if (!file) return;

    setIsUploading(true);
    setResult(null);
    setError(null);

    try {
      const response = await flotteService.importFactures(file);

      if (response.status === 200) {
        setResult({
          success: true,
          modifies: response.data?.modifies || [],
          figes: response.data?.figes || [],
          erreurs: {},
        });
      } else if (response.status === 207) {
        setResult({
          success: false,
          modifies: response.data?.modifies || [],
          figes: response.data?.figes || [],
          erreurs: response.data?.erreurs || {},
        });
      }

      fetchHistory();
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.erreurs) {
        setResult({
          success: false,
          modifies: errData.modifies || [],
          figes: errData.figes || [],
          erreurs: errData.erreurs || {},
        });
      } else {
        setError(
          errData?.message ||
            err.message ||
            "Une erreur est survenue lors de l'import.",
        );
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const erreurEntries = result ? Object.entries(result.erreurs) : [];
  const hasModifies = result && result.modifies.length > 0;
  const hasErreurs = erreurEntries.length > 0;
  // LS-738 : véhicules dont la facturation a été figée par cet import (sans anomalie)
  const figes = result?.figes || [];

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12 col-lg-8 offset-lg-2">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h5 className="mb-0">
                <i className="fas fa-file-import mr-2"></i>
                Import de factures
              </h5>
            </div>
            <div className="card-body">
              {/* Zone d'upload */}
              <div
                className="border rounded p-4 text-center mb-3"
                style={{
                  borderStyle: "dashed !important",
                  borderWidth: 2,
                  borderColor: file ? "#28a745" : "#dee2e6",
                  backgroundColor: file ? "#f8fff8" : "#f8f9fa",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="d-none"
                  onChange={handleFileChange}
                  accept=".csv,.xlsx,.xls"
                />
                {file ? (
                  <div>
                    <i
                      className="fas fa-file-excel fa-3x mb-2"
                      style={{ color: "#28a745" }}
                    ></i>
                    <p className="mb-1 font-weight-bold">{file.name}</p>
                    <small className="text-muted">
                      {(file.size / 1024).toFixed(1)} Ko
                    </small>
                  </div>
                ) : (
                  <div>
                    <i className="fas fa-cloud-upload-alt fa-3x mb-2 text-muted"></i>
                    <p className="mb-1">Cliquez ou glissez un fichier ici</p>
                    <small className="text-muted">
                      Formats acceptés : .csv, .xlsx, .xls
                    </small>
                  </div>
                )}
              </div>

              {/* Boutons d'action */}
              <div className="d-flex justify-content-end" style={{ gap: 8 }}>
                {file && (
                  <button
                    className="btn btn-default btn-sm"
                    onClick={handleReset}
                    disabled={isUploading}
                    type="button"
                  >
                    <i className="fas fa-times mr-1"></i>
                    Annuler
                  </button>
                )}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSubmit}
                  disabled={!file || isUploading}
                  type="button"
                >
                  {isUploading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm mr-2"
                        role="status"
                      ></span>
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload mr-1"></i>
                      Importer
                    </>
                  )}
                </button>
              </div>

              {/* Erreur générale */}
              {error && (
                <div className="alert alert-danger mt-3 mb-0">
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  {error}
                </div>
              )}

              {/* Résultats */}
              {result && (
                <div className="mt-3">
                  {/* Succès complet (200) */}
                  {result.success && !hasErreurs && (
                    <div className="alert alert-success mb-0">
                      <i className="fas fa-check-circle mr-2"></i>
                      Import réussi.
                      {hasModifies && (
                        <span>
                          {" "}
                          {result.modifies.length} véhicule
                          {result.modifies.length > 1 ? "s" : ""} modifié
                          {result.modifies.length > 1 ? "s" : ""}.
                        </span>
                      )}
                      {figes.length > 0 && (
                        <div className="mt-2" style={{ fontSize: "0.9em" }}>
                          <i className="fas fa-lock mr-2"></i>
                          {figes.length} véhicule{figes.length > 1 ? "s" : ""}{" "}
                          sans anomalie : date de facture, client facturé et
                          prix facturé figés ({figes.join(", ")}).
                        </div>
                      )}
                    </div>
                  )}

                  {/* Succès partiel (207) */}
                  {hasErreurs && (
                    <>
                      {hasModifies && (
                        <div className="alert alert-success">
                          <i className="fas fa-check-circle mr-2"></i>
                          <strong>
                            {result.modifies.length} véhicule
                            {result.modifies.length > 1 ? "s" : ""} modifié
                            {result.modifies.length > 1 ? "s" : ""}
                          </strong>{" "}
                          avec succès :
                          <div
                            className="mt-2 d-flex align-items-center"
                            style={{ gap: 6 }}
                          >
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              readOnly
                              value={result.modifies.join(", ")}
                              style={{ fontSize: "0.85em" }}
                            />
                            <CopyButton
                              text={result.modifies.join(", ")}
                              label="Copier les immatriculations modifiées"
                            />
                          </div>
                          {figes.length > 0 && (
                            <div className="mt-2" style={{ fontSize: "0.9em" }}>
                              <i className="fas fa-lock mr-2"></i>
                              {figes.length} véhicule
                              {figes.length > 1 ? "s" : ""} sans anomalie :
                              date de facture, client facturé et prix facturé
                              figés ({figes.join(", ")}).
                            </div>
                          )}
                        </div>
                      )}

                      <div className="alert alert-warning mb-0">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        <strong>
                          {erreurEntries.length} erreur
                          {erreurEntries.length > 1 ? "s" : ""}
                        </strong>{" "}
                        lors de l'import :
                        <CopyButton
                          text={erreurEntries.map(([ref]) => ref).join(", ")}
                          label="Copier toutes les références en erreur"
                        />
                        {(() => {
                          const grouped = {};
                          erreurEntries.forEach(([ref, msg]) => {
                            if (!grouped[msg]) grouped[msg] = [];
                            grouped[msg].push(ref);
                          });
                          return Object.entries(grouped).map(([msg, refs]) => (
                            <div
                              key={msg}
                              className="mt-2 p-2 bg-white border rounded"
                            >
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <strong style={{ fontSize: "0.9em" }}>
                                  {msg}
                                </strong>
                                <span className="badge badge-warning ml-2">
                                  {refs.length}
                                </span>
                              </div>
                              <div
                                className="d-flex align-items-center"
                                style={{ gap: 6 }}
                              >
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  readOnly
                                  value={refs.join(", ")}
                                  style={{ fontSize: "0.85em" }}
                                />
                                <CopyButton
                                  text={refs.join(", ")}
                                  label={`Copier les ${refs.length} référence(s)`}
                                />
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Historique des imports */}
          <div className="card shadow-sm mt-4">
            <div className="card-header bg-white d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                <i className="fas fa-history mr-2"></i>
                Historique des imports
              </h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchHistory}
                disabled={historyLoading}
              >
                <i
                  className={`fas fa-sync-alt mr-1 ${
                    historyLoading ? "fa-spin" : ""
                  }`}
                ></i>
                Actualiser
              </button>
            </div>
            <div className="card-body p-0">
              {historyError && (
                <div className="alert alert-danger m-3 mb-0">
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  {historyError}
                </div>
              )}

              {historyLoading && history.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <span
                    className="spinner-border spinner-border-sm mr-2"
                    role="status"
                  ></span>
                  Chargement...
                </div>
              ) : history.length === 0 && !historyError ? (
                <div className="text-center text-muted py-4">
                  Aucun import pour le moment.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th>Date</th>
                        <th>Fichier</th>
                        <th>Importé par</th>
                        <th className="text-right">Lignes</th>
                        <th className="text-right">Modifiés</th>
                        <th className="text-right">Erreurs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((item, idx) => {
                        const modifies = Array.isArray(item.modifies)
                          ? item.modifies
                          : [];
                        const erreurs = item.erreurs || {};
                        const erreursList = Object.entries(erreurs);
                        return (
                          <tr key={item.id ?? idx}>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {formatDateTime(item.importe_le)}
                            </td>
                            <td
                              style={{
                                maxWidth: 220,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={item.nom_fichier}
                            >
                              <i className="fas fa-file-excel text-muted mr-1"></i>
                              {item.nom_fichier || "-"}
                            </td>
                            <td>{item.importe_par || "-"}</td>
                            <td className="text-right">
                              {item.total_lignes ?? "-"}
                            </td>
                            <td className="text-right">
                              {modifies.length > 0 ? (
                                <span className="badge badge-success">
                                  {item.total_modifies ?? modifies.length}
                                </span>
                              ) : (
                                <span className="text-muted">
                                  {item.total_modifies ?? 0}
                                </span>
                              )}
                            </td>
                            <td className="text-right">
                              {erreursList.length > 0 ? (
                                <span className="badge badge-warning">
                                  {item.total_erreurs ?? erreursList.length}
                                </span>
                              ) : (
                                <span className="text-muted">
                                  {item.total_erreurs ?? 0}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Import;
